import { supabase } from '@/lib/supabase'
import type {
  ApiError,
} from '@/types'
import type { InvitationDTO } from '@/types/invitation'

/**
 * Invitation Service
 * Provides methods to interact with invitations via Supabase
 */
function createInvitationService() {
  /**
   * Create a new invitation to a mix
   */
  const createInvitation = async (invitation: {
    id: string
    mix_id: string
    mix_name: string
    sharer_id: string
    sharer_email: string
    sharer_name: string
    sharer_image: string
    token: string
    status: string
    expires_at: string
  }): Promise<void> => {
    const { error } = await supabase
      .from('invitations')
      .insert(invitation)

    if (error) {
      const apiError: ApiError = {
        message: error.message,
        status: 500,
      }
      throw apiError
    }
  }

  /**
   * Get Invitation details by ID (returns InvitationDTO with ownership)
   * Uses SECURITY DEFINER RPC so unauthenticated users can read invitation links.
   */
  const getInvitation = async (mixId: string, invitationId: string): Promise<InvitationDTO> => {
    const { data: { session } } = await supabase.auth.getSession()
    const userId = session?.user?.id

    const { data, error } = await supabase
      .rpc('get_invitation_by_id', {
        p_invitation_id: invitationId,
        p_mix_id: mixId,
      })

    if (error) {
      if (error.message?.includes('Invitation not found')) {
        window.location.href = '/404'
        throw new Error('Invitation not found')
      }
      const apiError: ApiError = {
        message: error.message,
        status: 500,
      }
      throw apiError
    }

    const row = data?.[0]
    if (!row) {
      window.location.href = '/404'
      throw new Error('Invitation not found')
    }

    const ownership = userId
      ? {
          isOwner: row.owner_id === userId,
          isCollaborator: row.contributors?.includes(userId) ?? false,
        }
      : null

    return {
      id: row.id,
      mixId: row.mix_id,
      mixName: row.mix_name,
      sharerName: row.sharer_name,
      sharerImage: row.sharer_image,
      usedByName: row.used_by_name || undefined,
      usedByImage: row.used_by_image || undefined,
      status: row.status,
      expiresAt: row.expires_at,
      usedAt: row.used_at,
      createdAt: row.created_at,
      updatedAt: row.updated_at,
      isUsedByMe: userId ? row.used_by_id === userId : false,
      ownership,
    }
  }

  /**
   * Accept an invitation
   * Uses SECURITY DEFINER RPC for atomic multi-table update.
   */
  const acceptInvitation = async (
    invitationId: string,
    mixId: string,
    userName: string,
    userImage: string
  ): Promise<void> => {
    const { error } = await supabase.rpc('accept_invitation', {
      p_invitation_id: invitationId,
      p_mix_id: mixId,
      p_user_name: userName,
      p_user_image: userImage,
    })

    if (error) {
      const message = error.message || ''

      if (message.includes('Invitation not found')) {
        window.location.href = '/404'
        throw new Error('Invitation not found')
      }

      const apiError: ApiError = {
        message: message,
        status: 500,
      }
      throw apiError
    }
  }

  return {
    createInvitation,
    getInvitation,
    acceptInvitation,
  }
}

export const invitationService = createInvitationService()
